/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends › Approvals funnel (docs/CONTRACT-fleet-governance.md sections 7.4, 9.1 and 12.3): per app and tool, proposed,
 * confirmed, awaiting, approved, rejected, cancelled and expired, with the person's and the approver's p50 and p95 time to
 * decide, from GET /v1/admin/governance/funnel (sorted and paged by the service). A row opens its audit rows (`action.`,
 * that app and tool).
 */
import { useState } from 'react';
import { Workflow } from 'lucide-react';
import { sortParam, toggleSort, type SortState } from '../../lib/fleet';
import { ACTION_CHOICE, durationText, pctText, trendsApi, trendsExportHref, type GovFunnelAnswer, type GovFunnelRow } from '../../lib/trends';
import { EmptyState, Panel, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { usePagedList } from '../pulse/common';
import { ExportLink } from '../fleet/parts';
import { SectionError, type SectionProps } from './parts';

const DEFAULT_SORT: SortState = { key: 'proposed', direction: 'desc' };
const WORD_COLUMNS = new Set(['app', 'tool']);

const pair = (p50: number | null, p95: number | null) => (p50 === null && p95 === null ? '—' : `${durationText(p50)} / ${durationText(p95)}`);

export function TrendsFunnel({ query, filter, onShowRows }: SectionProps) {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const params = { ...query, sort: sortParam(sort) };
  const list = usePagedList((cursor) => trendsApi.funnel({ ...params, cursor, limit: 50 }), `funnel|${JSON.stringify(params)}`);
  const answer = list.load.state === 'ready' ? (list.load.data as GovFunnelAnswer) : null;

  const open = (row: GovFunnelRow) => {
    if (!answer) return;
    onShowRows({ title: `Approvals · ${row.app} · ${row.tool}`, choices: [ACTION_CHOICE], app: row.app, tool: row.tool, range: { from: answer.range.from, to: answer.range.to } });
  };

  const number = (key: keyof GovFunnelRow, header: string, hideOnPhone = false): Column<GovFunnelRow> => ({
    key, header, numeric: true, sortable: true, hideOnPhone, cell: (row) => count(row[key] as number),
  });
  const columns: Array<Column<GovFunnelRow>> = [
    { key: 'app', header: 'App', sortable: true, cell: (row) => <Tag>{row.app}</Tag> },
    { key: 'tool', header: 'Tool', sortable: true, mono: true, cell: (row) => row.tool },
    number('proposed', 'Proposed'),
    number('confirmed', 'Confirmed'),
    number('awaiting', 'Awaiting', true),
    number('approved', 'Approved'),
    number('rejected', 'Rejected', true),
    number('cancelled', 'Cancelled', true),
    { key: 'expired', header: 'Expired', numeric: true, sortable: true, cell: (row) => `${count(row.expired)}${row.expiredPct === null ? '' : ` (${pctText(row.expiredPct, 0)})`}` },
    { key: 'personP50s', header: 'Person p50 / p95', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => pair(row.personP50s, row.personP95s) },
    { key: 'approverP50s', header: 'Approver p50 / p95', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => pair(row.approverP50s, row.approverP95s) },
  ];

  return (
    <Panel flush title="Approvals funnel" description="Each tool's cards from proposal to decision, and how long people and approvers took; a row opens its audit rows" actions={<ExportLink href={trendsExportHref('funnel', filter)} />}>
      {list.load.state === 'failed' ? <div className="ui-panel-pad"><SectionError title="The approvals funnel could not be loaded" error={list.load.error} onRetry={list.reload} retrying={list.reloading} /></div> : (
        <Table
          label="Approvals funnel"
          columns={columns}
          rows={list.items}
          rowKey={(row) => `${row.app}|${row.tool}`}
          loading={list.load.state === 'loading'}
          skeletonRows={4}
          onRowClick={open}
          sort={{ key: sort.key, direction: sort.direction, onSort: (key) => setSort(toggleSort(sort, key, !WORD_COLUMNS.has(key))) }}
          more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
          empty={<EmptyState compact icon={Workflow} title="No approval cards in this range">Cards appear here as Gyro proposes writes for people to confirm.</EmptyState>}
        />
      )}
      {list.moreError ? <div className="ui-panel-pad ui-panel-pad--top"><SectionError title="The next page could not be loaded" error={list.moreError} /></div> : null}
    </Panel>
  );
}
